import React, { useState, useRef, useEffect } from 'react';
import { ChevronRight, Send, Sparkles, Leaf, ExternalLink } from 'lucide-react';
import { useApp } from '../context/AppContext';

const ECO_TIER_COLOR = {
  A: '#1B7340', B: '#4A9F5B', C: '#996B3D', D: '#B85C3B', E: '#7A0E2E',
};

// State the tier in words, not a letter grade.
const ECO_TIER_LABEL = {
  A: 'Highly sustainable', B: 'Eco-conscious', C: 'Moderate impact',
  D: 'High impact', E: 'Very high impact',
};

const CURRENCY_SYMBOL = { INR: '₹', USD: '$', GBP: '£', AED: 'AED' };

const GREETING = {
  role: 'assistant',
  reply:
    "Hi, I'm Atelia, your sustainability stylist. Tell me what you're shopping for and I'll find pieces you'll love, with the fabric story behind each one.",
  suggested_answers: [
    { label: 'Summer wedding outfit', value: "I need an outfit for a summer wedding" },
    { label: 'Breathable everyday wear', value: "I want breathable everyday clothing" },
    { label: 'Office/formal', value: "I'm looking for something formal for the office" },
    { label: 'Most sustainable basics', value: "Show me the most sustainable wardrobe basics" },
  ],
  products: [],
};

const TypingDots = () => (
  <div className="flex items-center gap-1.5 px-1 py-1">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="w-2 h-2 rounded-full bg-white/50 animate-bounce"
        style={{ animationDelay: `${i * 0.15}s`, animationDuration: '0.9s' }}
      />
    ))}
  </div>
);

const ProductCard = ({ p }) => {
  // "Great for", first occasion, title-cased
  const occasion = (p.occasions && p.occasions.length) ? p.occasions[0] : null;
  const formality = p.formality || null;
  return (
    <a
      href={p.product_url || '#'}
      target="_blank"
      rel="noreferrer"
      className="block bg-white/[0.06] border border-white/10 rounded-2xl overflow-hidden
        hover:bg-white/[0.1] hover:border-white/25 transition-all group"
    >
      {/* Image-first, looks are the point */}
      <div className="relative w-full aspect-[4/5] bg-white/5 overflow-hidden">
        {p.image_url ? (
          <img
            src={p.image_url}
            alt={p.name}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-white/20">
            <Sparkles size={22} />
          </div>
        )}
        {p.eco_tier && ECO_TIER_LABEL[p.eco_tier] && (
          <span
            className="absolute top-2.5 right-2.5 text-[10px] font-semibold text-white px-2 py-1 rounded-lg
              flex items-center gap-1 shadow-lg backdrop-blur-sm"
            style={{ backgroundColor: (ECO_TIER_COLOR[p.eco_tier] || '#555') + 'ee' }}
          >
            <Leaf size={10} /> {ECO_TIER_LABEL[p.eco_tier]}
          </span>
        )}
      </div>

      <div className="p-3.5">
        <p className="text-white/50 text-[10px] uppercase tracking-[0.15em] truncate">{p.brand}</p>
        <p className="text-white text-sm font-medium leading-snug line-clamp-2 mt-0.5">{p.name}</p>

        {/* Fabric breakdown, the moat, stated plainly */}
        {p.composition && (
          <p className="text-white/55 text-[11px] mt-1 flex items-center gap-1">
            <Leaf size={10} className="text-white/40" /> {p.composition}
          </p>
        )}

        {(occasion || formality) && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {occasion && (
              <span className="text-[10px] text-white/80 bg-white/10 px-2 py-0.5 rounded-full capitalize">
                {occasion.replace('-', ' ')}
              </span>
            )}
            {formality && (
              <span className="text-[10px] text-white/80 bg-white/10 px-2 py-0.5 rounded-full capitalize">
                {formality.replace('-', ' ')}
              </span>
            )}
          </div>
        )}

        {p.why_for_you && (
          <p className="text-white/65 text-xs leading-relaxed mt-2 line-clamp-3">{p.why_for_you}</p>
        )}

        <div className="mt-2.5 flex items-center justify-end">
          <span className="text-white/40 text-[10px] uppercase tracking-wider flex items-center gap-1 group-hover:text-white/80 transition-colors">
            View <ExternalLink size={10} />
          </span>
        </div>
      </div>
    </a>
  );
};

const ChatAdvisorScreen = () => {
  const { setCurrentScreen, selectedCountry } = useApp();
  const country = selectedCountry || 'India';
  const currency = { India: 'INR', USA: 'USD', UK: 'GBP', UAE: 'AED' }[country] || 'INR';

  const [messages, setMessages] = useState([GREETING]);
  const [profile, setProfile] = useState({});
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const lastAssistant = [...messages].reverse().find((m) => m.role === 'assistant');
  const chips = !loading && lastAssistant ? (lastAssistant.suggested_answers || []) : [];

  const send = async (text) => {
    const content = (text ?? input).trim();
    if (!content || loading) return;

    const userMsg = { role: 'user', content };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setInput('');
    setLoading(true);

    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('auth_token');
      const wire = nextMessages.map((m) => ({
        role: m.role,
        content: m.role === 'assistant' ? (m.reply || m.content || '') : m.content,
      }));

      // Product ids already shown this conversation, so "Show 5 more" returns fresh picks.
      const shownIds = nextMessages
        .flatMap((m) => m.products || [])
        .map((p) => p.id)
        .filter(Boolean);

      const res = await fetch(`${API_URL}/api/chat/advisor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ messages: wire, preference_profile: profile, country, shown_ids: shownIds }),
      });

      if (!res.ok) throw new Error('advisor failed');
      const data = await res.json();

      setProfile(data.preference_profile || profile);
      const replyText = [data.reply, data.next_question].filter(Boolean).join('\n\n');
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          reply: replyText,
          suggested_answers: data.suggested_answers || [],
          products: data.products || [],
        },
      ]);
    } catch (e) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', reply: "Sorry, I lost my thread for a moment. Could you say that again?", suggested_answers: [], products: [] },
      ]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black flex flex-col">
      {/* Header */}
      <div className="px-5 pt-5 pb-3 flex items-center gap-3 border-b border-white/10 flex-shrink-0">
        <button
          onClick={() => setCurrentScreen('home')}
          className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center hover:bg-white/20 transition-all"
        >
          <ChevronRight size={16} className="text-white rotate-180" />
        </button>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-xl bg-white/10 flex items-center justify-center">
            <Sparkles size={16} className="text-white" />
          </div>
          <div>
            <p className="text-white text-sm font-bold uppercase tracking-[0.2em] leading-none">Atelia Stylist</p>
            <p className="text-white/40 text-[10px] tracking-wide mt-0.5">Fabric-intelligent · Eco-first</p>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-5 space-y-4">
        {messages.map((m, idx) => (
          <div key={idx} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`${m.role === 'user' ? 'max-w-[80%]' : 'max-w-[88%]'} space-y-3`}>
              <div
                className={
                  m.role === 'user'
                    ? 'bg-white text-black rounded-2xl rounded-br-md px-4 py-2.5 text-sm leading-relaxed'
                    : 'bg-white/[0.07] text-white rounded-2xl rounded-bl-md px-4 py-3 text-sm leading-relaxed whitespace-pre-line border border-white/10'
                }
              >
                {m.role === 'user' ? m.content : m.reply}
              </div>

              {m.products && m.products.length > 0 && (
                <div className="grid grid-cols-2 gap-2.5">
                  {m.products.map((p) => (
                    <ProductCard key={p.id || p.name} p={p} />
                  ))}
                </div>
              )}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-white/[0.07] border border-white/10 rounded-2xl rounded-bl-md px-3 py-2">
              <TypingDots />
            </div>
          </div>
        )}
      </div>

      {/* Suggestion chips + input */}
      <div className="flex-shrink-0 border-t border-white/10 bg-black px-4 pt-3 pb-5">
        {chips.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {chips.map((c, i) => (
              <button
                key={i}
                onClick={() => send(c.value)}
                className="px-3.5 py-2 rounded-full border border-white/25 text-white/90 text-xs
                  hover:bg-white hover:text-black transition-all"
              >
                {c.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            rows={1}
            placeholder="Tell me what you're looking for…"
            className="flex-1 resize-none max-h-28 px-4 py-3 bg-white/10 border border-white/20 rounded-2xl
              text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-white/40 transition-all"
          />
          <button
            onClick={() => send()}
            disabled={!input.trim() || loading}
            className="w-12 h-12 flex-shrink-0 bg-white text-black rounded-2xl flex items-center justify-center
              hover:bg-white/90 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Send size={18} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChatAdvisorScreen;
